import type { ModelAssetNode } from '../assets/model-asset-types'

/**
 * 一次后端 project-model expand 内部使用的 direct child RPC 去重状态。
 *
 * @internal 该状态只允许由加载编排层跨 pass 传递，不属于公共加载结果。
 */
export interface InternalPuppyAssemblyExpandTransactionState {
  /** 已成功 RPC 的外部引用定位键到实际插入节点 key 的只读快照。 */
  readonly insertedNodeKeyByReferenceLocator: ReadonlyMap<string, string>
}

/**
 * 创建隔离的 expand 内部事务状态。
 *
 * @internal
 * @param entries 已有定位映射；函数始终复制，避免调用方后续修改影响事务。
 * @returns 可安全传给下一轮 expand 的状态快照。
 */
export function createInternalPuppyAssemblyExpandTransactionState(
  entries: ReadonlyMap<string, string> = new Map(),
): InternalPuppyAssemblyExpandTransactionState {
  return {
    insertedNodeKeyByReferenceLocator: new Map(entries),
  }
}

/**
 * 创建只服务同一次 expand 事务 RPC 去重的外部 reference locator。
 *
 * @internal
 * @param rootNodeKey 当前装配文档作用域根 key。
 * @param referenceNodeKey 装配文档 direct child 的结构定位 key。
 * @returns 无歧义的事务内 locator。
 */
export function createInternalPuppyAssemblyDirectChildReferenceLocator(
  rootNodeKey: string,
  referenceNodeKey: string,
): string {
  return JSON.stringify([rootNodeKey, referenceNodeKey])
}

/**
 * 查找仍可复用的事务插入记录。
 *
 * 映射只有在节点仍是当前 target root 的非 embedded 直属 child 时才有效；否则必须重新 RPC，
 * 防止重物化移除节点后旧 locator 错误阻止恢复。
 *
 * @internal
 * @param input 当前事务状态、目标 root 与 locator。
 * @returns 仍存在的外部 child key；记录失效或不存在时返回 null。
 */
export function resolveReusableInternalPuppyAssemblyInsertedNodeKey(input: {
  readonly state: InternalPuppyAssemblyExpandTransactionState
  readonly targetRoot: ModelAssetNode
  readonly referenceLocator: string
}): string | null {
  const insertedNodeKey = input.state.insertedNodeKeyByReferenceLocator.get(input.referenceLocator)
  if (!insertedNodeKey) {
    return null
  }

  const insertedChild = input.targetRoot.children.find(child => child.nodeKey === insertedNodeKey)
  return insertedChild && insertedChild.assetSource?.kind !== 'embedded-puppy-occurrence'
    ? insertedNodeKey
    : null
}

/**
 * 记录一次已成功插入的外部 direct child，并返回全新的事务状态快照。
 *
 * @internal
 * @param input 当前状态、locator 与实际插入节点 key。
 * @returns 包含新记录且与输入 Map 隔离的状态。
 */
export function recordInternalPuppyAssemblyInsertedNode(input: {
  readonly state: InternalPuppyAssemblyExpandTransactionState
  readonly referenceLocator: string
  readonly insertedNodeKey: string
}): InternalPuppyAssemblyExpandTransactionState {
  const nextEntries = new Map(input.state.insertedNodeKeyByReferenceLocator)
  nextEntries.set(input.referenceLocator, input.insertedNodeKey)
  return createInternalPuppyAssemblyExpandTransactionState(nextEntries)
}
